import { useMemo, useState } from "react";
import { IPlayerDetail } from "../../models/IPlayer";
import { IPromoCode } from "../../models/IPromoCode";
import { CheckOctagon, DangerOctagon, Pencil } from "@mynaui/icons-react";
import { updateTeamMemberStatus, movePlayerToTeam } from "../../api/tournaments";
import {
  Button,
  DescriptionList,
  DescriptionItem,
  Dialog,
  Input,
  Select,
  Tag,
  TagTone,
  Textarea,
  buttonClassName,
} from "../../ui";
import "./styles.scss";

type Fee = {
  id: string;
  amount: number;
  currency: string;
  description: string;
  displayOrder: number;
  isEnabled: boolean;
  perTeam: boolean;
};

type TeamOption = {
  id: string;
  teamName: string;
};

type PlayerStatusDialogProps = {
  teamId: string;
  item: IPlayerDetail;
  isOpen: boolean;
  fees?: Fee[];
  promoCodes?: IPromoCode[];
  teams?: TeamOption[];
  hasDividedTeams?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const registrationTone = (status: string): TagTone =>
  status.includes("Pending") ? "warn" : status === "Confirmed" ? "ok" : "danger";

const paymentTone = (status: string): TagTone =>
  status.includes("Pending") || status.includes("Cash") ? "warn" : status === "Paid" ? "ok" : "danger";

const PlayerStatusDialog = (props: PlayerStatusDialogProps) => {
  const [isEdit, setIsEdit] = useState(false);
  const [status, setStatus] = useState(props.item.registrationStatus);
  const [paymentStatus, setPaymentStatus] = useState(props.item.paymentStatus);
  const [notes, setNotes] = useState(props.item.notes);
  const [promoCode, setPromoCode] = useState(props.item.promoCode ?? "");
  const [feeId, setFeeId] = useState(props.item.fee?.id ?? "");
  const [paymentMethod, setPaymentMethod] = useState(props.item.paymentMethod ?? "");
  const [selectedTeamId, setSelectedTeamId] = useState(props.item.teamId);
  const [firstName, setFirstName] = useState(props.item.firstName ?? "");
  const [lastName, setLastName] = useState(props.item.lastName ?? "");
  const [saving, setSaving] = useState(false);

  const isUnregistered =
    (!props.item.playerId || props.item.playerId === "00000000-0000-0000-0000-000000000000")
    && (!props.item.id || props.item.id === "00000000-0000-0000-0000-000000000000");

  const selectedFee = useMemo(() => {
    return props.fees?.find((f) => f.id === feeId);
  }, [feeId, props.fees]);

  const selectedPromo = useMemo(() => {
    if (!promoCode) return null;
    return props.promoCodes?.find((p) => p.code === promoCode) ?? null;
  }, [promoCode, props.promoCodes]);

  const totalAfterPromo = useMemo(() => {
    if (!selectedFee) return null;
    const amount = selectedFee.amount;
    if (!selectedPromo) return amount;
    if (selectedPromo.discountPercentage) {
      return Math.max(0, amount - (amount * selectedPromo.discountPercentage) / 100);
    }
    if (selectedPromo.discountAmount) {
      return Math.max(0, amount - selectedPromo.discountAmount);
    }
    return amount;
  }, [selectedFee, selectedPromo]);

  const handleClose = () => {
    props.onClose();
  };

  const handleCancel = () => {
    setIsEdit(false);
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const payload: Parameters<typeof updateTeamMemberStatus>[2] = {
        status,
        notes,
        paymentStatus,
        // Admin-set methods are limited to instapay/cash; gateway-set values
        // (fawry/balance) only land here if the existing value is preserved.
        paymentMethod: paymentMethod === "" ? null : paymentMethod,
        promoCode: promoCode || null,
        feeId: feeId || null,
      };
      if (isUnregistered) {
        payload.playerName = `${firstName.trim()} ${lastName.trim()}`.trim();
      }
      await updateTeamMemberStatus(props.teamId, props.item.teamMemberId, payload);
      if (selectedTeamId !== props.item.teamId) {
        await movePlayerToTeam({ teamMemberId: props.item.teamMemberId, targetTeamId: selectedTeamId });
      }
      props.onConfirm();
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const items: DescriptionItem[] = [];

  if (isUnregistered && isEdit) {
    items.push(
      {
        label: "First Name",
        value: (
          <Input type="text" aria-label="First Name" value={firstName} onChange={(evt) => setFirstName(evt.target.value)} />
        ),
      },
      {
        label: "Last Name",
        value: (
          <Input type="text" aria-label="Last Name" value={lastName} onChange={(evt) => setLastName(evt.target.value)} />
        ),
      }
    );
  }

  if (!isUnregistered) {
    items.push({ label: "Gender", value: props.item.gender }, { label: "Nationality", value: props.item.nationality });
  }

  if (props.hasDividedTeams && props.teams && props.teams.length > 0) {
    items.push({
      label: "Team",
      value: isEdit ? (
        <Select block aria-label="Team" value={selectedTeamId} onChange={(evt) => setSelectedTeamId(evt.target.value)}>
          {props.teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.teamName === "__INDIVIDUAL_REGISTRATION__" ? "Unassigned" : t.teamName}
            </option>
          ))}
        </Select>
      ) : props.item.teamName === "__INDIVIDUAL_REGISTRATION__" ? (
        "Unassigned"
      ) : (
        props.item.teamName
      ),
    });
  }

  items.push({
    label: "Chosen Fee",
    value:
      isEdit && props.fees && props.fees.length > 0 ? (
        <Select block aria-label="Chosen Fee" value={feeId} onChange={(evt) => setFeeId(evt.target.value)}>
          <option value="">No fee</option>
          {props.fees.map((fee) => (
            <option key={fee.id} value={fee.id}>
              {fee.description} - {fee.amount} {fee.currency}
            </option>
          ))}
        </Select>
      ) : props.item.fee ? (
        `${props.item.fee.description} - ${props.item.fee.amount} ${props.item.fee.currency}`
      ) : (
        "-"
      ),
  });

  items.push({
    label: "Promo Code",
    value: isEdit ? (
      <Select block aria-label="Promo Code" value={promoCode} onChange={(evt) => setPromoCode(evt.target.value)}>
        <option value="">No promo code</option>
        {props.promoCodes
          ?.filter((p) => p.isActive)
          .map((p) => (
            <option key={p.id} value={p.code}>
              {p.code}
              {p.discountPercentage ? ` (${p.discountPercentage}% off)` : ""}
              {p.discountAmount ? ` (${p.discountAmount} off)` : ""}
            </option>
          ))}
      </Select>
    ) : (
      props.item.promoCode ?? "-"
    ),
  });

  items.push({
    label: "Total After Promo",
    value: totalAfterPromo != null && selectedFee ? `${totalAfterPromo} ${selectedFee.currency}` : "-",
  });

  if (!isUnregistered) {
    items.push({
      label: "Tournament Consent",
      value: props.item.consent ? (
        <Tag tone="ok" icon={<CheckOctagon />}>
          Consented
        </Tag>
      ) : (
        <Tag tone="warn" icon={<DangerOctagon />}>
          Not Consented
        </Tag>
      ),
    });
  }

  items.push({
    label: "Registration Status",
    value: isEdit ? (
      <Select block aria-label="Registration Status" value={status} onChange={(evt) => setStatus(evt.target.value)}>
        <option className="Pending Payment">Pending Payment</option>
        <option className="Canceled">Canceled</option>
        <option className="Confirmed">Confirmed</option>
      </Select>
    ) : (
      <Tag tone={registrationTone(props.item.registrationStatus)}>{props.item.registrationStatus}</Tag>
    ),
  });

  items.push({
    label: "Payment Status",
    value: isEdit ? (
      <Select block aria-label="Payment Status" value={paymentStatus} onChange={(evt) => setPaymentStatus(evt.target.value)}>
        <option className="Pending">Pending</option>
        <option className="Paid">Paid</option>
        <option className="Not Paid">Not Paid</option>
        <option className="Cash Payment On Field">Cash Payment On Field</option>
      </Select>
    ) : (
      <Tag tone={paymentTone(props.item.paymentStatus)}>{props.item.paymentStatus}</Tag>
    ),
  });

  const pushPaymentMethod = () => {
    // Existing methods set by the gateway code paths (fawry/balance)
    // are shown read-only — admins shouldn't overwrite them. Anything
    // else (no method, or already instapay/cash) gets the editable
    // dropdown so it can be set or changed.
    const current = props.item.paymentMethod;
    const isLockedMethod = current === "fawry" || current === "balance";
    const labelFor = (m: string | null | undefined) =>
      m === "fawry" ? "Online (Fawry)" :
      m === "instapay" ? "InstaPay" :
      m === "cash" ? "Cash" :
      m === "balance" ? "Clash Balance" :
      m || "-";
    const toneFor = (m: string | null | undefined): TagTone =>
      m === "fawry" || m === "balance" ? "ok" :
      m === "instapay" || m === "cash" ? "warn" : "neutral";

    if (isEdit && !isLockedMethod) {
      items.push({
        label: "Payment Method",
        value: (
          <Select block aria-label="Payment Method" value={paymentMethod} onChange={(evt) => setPaymentMethod(evt.target.value)}>
            <option value="">Not set</option>
            <option value="instapay">InstaPay</option>
            <option value="cash">Cash</option>
          </Select>
        ),
      });
    } else if (current) {
      items.push({ label: "Payment Method", value: <Tag tone={toneFor(current)}>{labelFor(current)}</Tag> });
    }
  };
  pushPaymentMethod();

  if (props.item.fawryReferenceNumber) {
    items.push({
      label: "Fawry Reference",
      value: <code className="player-status-dialog__ref">{props.item.fawryReferenceNumber}</code>,
    });
  }

  if (props.item.receiptScreenshot) {
    items.push({
      label: "Payment Screenshot",
      value: (
        <a
          href={props.item.receiptScreenshot}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClassName({ variant: "link", size: "sm" })}
        >
          View Screenshot
        </a>
      ),
    });
  }

  items.push({
    label: "Admin Notes",
    value: isEdit ? (
      <Textarea aria-label="Admin Notes" value={notes} onChange={(evt) => setNotes(evt.target.value)} />
    ) : (
      props.item.notes ?? "-"
    ),
  });

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      placement="side"
      size="sm"
      title={`${props.item.firstName} ${props.item.lastName ?? ""}`}
      subtitle={isUnregistered ? <Tag>Non-registered</Tag> : undefined}
      footer={
        isEdit ? (
          <>
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} loading={saving}>
              Confirm
            </Button>
          </>
        ) : undefined
      }
    >
      {!isEdit && (
        <div className="player-status-dialog__actions">
          <Button size="sm" variant="tint" icon={<Pencil />} onClick={() => setIsEdit(true)}>
            Edit
          </Button>
        </div>
      )}
      <DescriptionList columns={1} items={items} />
    </Dialog>
  );
};

export default PlayerStatusDialog;
